/**
 * Is this URL path part of the public, no-login Lowe's form?
 *
 * True when the FIRST path segment is "lowes" in any letter case: the page,
 * its save endpoint, their `.data` requests, and any mistyped address under
 * them. Routes match without regard to case, so every place that treats the
 * public form specially (the header, the 404 page, browser Sentry) has to as
 * well, and they all ask here so they cannot drift apart. Never true for the
 * staff module at /apps/lowes. Redaction and the rate-limit budget ask the
 * wider looksLikeLowesPath below.
 *
 * Pure and dependency-free: the express server, the app and the browser all
 * import it.
 */
export function isLowesPublicPath(pathname: string): boolean {
	const first = firstSegment(decodeEscapes(pathname))
	return first.toLowerCase().replace(/\.data$/, '') === 'lowes'
}

/**
 * The wider test, for REDACTION (the request log, server Sentry) and the
 * Lowe's rate-limit BUDGET only: does the first path segment START WITH
 * "lowes", in any letter case, once control characters are removed?
 *
 * `/lowes%00?ref=X` and `/lowes;x?ref=X` are not the form (they are 404s), so
 * isLowesPublicPath says no, but someone can still send them with a referral
 * code in the query. Saying yes too often here only hides a query string or
 * moves a request to the form's own budget; no real route's first segment
 * starts with "lowes" except the form's own. The header and the 404 page keep
 * the exact test, so what a person sees is unchanged.
 */
export function looksLikeLowesPath(pathname: string): boolean {
	// C0 controls, DEL and the C1 controls: what %00 or %0A decode to.
	const path = decodeEscapes(pathname).replace(/[\x00-\x1f\x7f-\x9f]/g, '')
	return firstSegment(path).toLowerCase().startsWith('lowes')
}

// Decode escape by escape, so one malformed escape later in the address
// cannot hide an encoded "lowes" in front of it.
function decodeEscapes(pathname: string) {
	return pathname.replace(/(?:%[0-9a-f]{2})+/gi, (escapes) => {
		try {
			return decodeURIComponent(escapes)
		} catch {
			return escapes
		}
	})
}

function firstSegment(path: string) {
	return path.split(/[?#]/)[0]!.split('/').find(Boolean) ?? ''
}
